import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  MessageSquare,
  Phone,
  Webhook,
  Timer,
  GitBranch,
  Filter,
  Database,
  Zap,
  Settings,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  Layers,
  Bell,
  FileText,
  Calendar,
  Users,
  BarChart3,
  Code,
  Globe
} from 'lucide-react';
import { WorkflowStepType } from '../types/workflow';

interface StepTemplate {
  type: WorkflowStepType;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  category: string;
  defaultConfig: Record<string, any>;
}

interface StepCategory {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  steps: StepTemplate[];
}

const stepTemplates: StepCategory[] = [
  {
    id: 'communication',
    name: 'Communication',
    icon: MessageSquare,
    steps: [
      {
        type: 'email',
        name: 'Send Email',
        description: 'Send personalized email messages',
        icon: Mail,
        category: 'communication',
        defaultConfig: {
          subject: '',
          body: '',
          personalization: true
        }
      },
      {
        type: 'sms',
        name: 'Send SMS',
        description: 'Send text messages to contacts',
        icon: MessageSquare,
        category: 'communication',
        defaultConfig: {
          message: '',
          personalization: true
        }
      },
      {
        type: 'notification',
        name: 'Send Notification',
        description: 'Send push notifications or alerts',
        icon: Bell,
        category: 'communication',
        defaultConfig: {
          title: '',
          message: '',
          type: 'info'
        }
      }
    ]
  },
  {
    id: 'logic',
    name: 'Logic & Control',
    icon: GitBranch,
    steps: [
      {
        type: 'condition',
        name: 'Condition',
        description: 'Branch workflow based on conditions',
        icon: GitBranch,
        category: 'logic',
        defaultConfig: {
          conditions: [],
          trueAction: 'continue',
          falseAction: 'skip'
        }
      },
      {
        type: 'delay',
        name: 'Wait/Delay',
        description: 'Pause workflow execution',
        icon: Timer,
        category: 'logic',
        defaultConfig: {
          duration: 1,
          unit: 'hours'
        }
      },
      {
        type: 'loop',
        name: 'Loop',
        description: 'Repeat actions for multiple items',
        icon: Layers,
        category: 'logic',
        defaultConfig: {
          iterations: 1,
          breakCondition: null
        }
      }
    ]
  },
  {
    id: 'data',
    name: 'Data & Integration',
    icon: Database,
    steps: [
      {
        type: 'webhook',
        name: 'Webhook',
        description: 'Call external APIs or webhooks',
        icon: Webhook,
        category: 'data',
        defaultConfig: {
          url: '',
          method: 'POST',
          headers: {},
          body: {}
        }
      },
      {
        type: 'api_call',
        name: 'API Call',
        description: 'Make HTTP requests to external services',
        icon: Globe,
        category: 'data',
        defaultConfig: {
          url: '',
          method: 'GET',
          headers: {},
          authentication: 'none'
        }
      },
      {
        type: 'data_transform',
        name: 'Transform Data',
        description: 'Modify or transform data',
        icon: Code,
        category: 'data',
        defaultConfig: {
          transformations: [],
          outputFormat: 'json'
        }
      }
    ]
  },
  {
    id: 'triggers',
    name: 'Triggers & Events',
    icon: Zap,
    steps: [
      {
        type: 'trigger',
        name: 'Manual Trigger',
        description: 'Start workflow manually',
        icon: Zap,
        category: 'triggers',
        defaultConfig: {
          triggerType: 'manual',
          requireConfirmation: false
        }
      }
    ]
  },
  {
    id: 'advanced',
    name: 'Advanced',
    icon: Settings,
    steps: [
      {
        type: 'approval',
        name: 'Approval Step',
        description: 'Require human approval to continue',
        icon: Users,
        category: 'advanced',
        defaultConfig: {
          approvers: [],
          timeoutDuration: 24,
          timeoutAction: 'reject'
        }
      },
      {
        type: 'custom',
        name: 'Custom Action',
        description: 'Execute custom code or logic',
        icon: Code,
        category: 'advanced',
        defaultConfig: {
          code: '',
          language: 'javascript',
          timeout: 30000
        }
      }
    ]
  }
];

interface StepPaletteProps {
  onStepDragStart?: (step: StepTemplate) => void;
  onStepDragEnd?: () => void;
  className?: string;
}

export const StepPalette: React.FC<StepPaletteProps> = ({
  onStepDragStart,
  onStepDragEnd,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['communication', 'logic'])
  );

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const filteredCategories = stepTemplates.map(category => ({
    ...category,
    steps: category.steps.filter(step =>
      step.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      step.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.steps.length > 0);

  const handleDragStart = (e: React.DragEvent, step: StepTemplate) => {
    e.dataTransfer.setData('application/json', JSON.stringify(step));
    e.dataTransfer.effectAllowed = 'copy';
    onStepDragStart?.(step);
  };

  const handleDragEnd = () => {
    onStepDragEnd?.();
  };

  return (
    <div className={`w-80 h-full bg-neutral-900/90 backdrop-blur-sm border-r border-neutral-800 flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-neutral-800">
        <h2 className="text-lg font-bold text-white mb-3">Workflow Steps</h2>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search steps..."
            className="w-full pl-10 pr-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredCategories.map((category) => {
          const CategoryIcon = category.icon;
          const isExpanded = expandedCategories.has(category.id);

          return (
            <div key={category.id} className="space-y-2">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-2 text-neutral-300 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-2">
                  <CategoryIcon className="w-4 h-4" />
                  <span className="font-medium">{category.name}</span>
                  <span className="text-xs text-neutral-500">({category.steps.length})</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Category Steps */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-1 ml-2"
                  >
                    {category.steps.map((step) => {
                      const StepIcon = step.icon;
                      
                      return (
                        <motion.div
                          key={step.type}
                          draggable
                          onDragStart={(e) => handleDragStart(e as any, step)}
                          onDragEnd={handleDragEnd}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="group p-3 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 hover:border-neutral-600 rounded-lg cursor-grab active:cursor-grabbing transition-all duration-200"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-200">
                              <StepIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-white text-sm group-hover:text-blue-300 transition-colors">
                                {step.name}
                              </h4>
                              <p className="text-xs text-neutral-400 mt-1 line-clamp-2">
                                {step.description}
                              </p>
                            </div>
                          </div>
                          
                          {/* Drag Indicator */}
                          <div className="mt-2 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex gap-1">
                              {[...Array(6)].map((_, i) => (
                                <div key={i} className="w-1 h-1 bg-neutral-500 rounded-full" />
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {filteredCategories.length === 0 && (
          <div className="text-center py-8">
            <Search className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
            <p className="text-neutral-400 text-sm">
              No steps found matching "{searchTerm}"
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 text-center">
          Drag steps to the canvas to build your workflow
        </div>
      </div>
    </div>
  );
}; 